/** Optional overrides preserve the original appearance of saved table widgets. */
export interface TableTextStyle {
  color?: string;
  fontFamily?: string;
  fontSize?: number;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
}

export type TableTextGroup = 'title' | 'header' | 'body';
export type TableTextStyles = Partial<Record<TableTextGroup, TableTextStyle>>;

const FONT_FALLBACKS: Record<string, string> = {
  'dm sans': "'DM Sans', Arial, sans-serif",
  'helvetica neue': "'Helvetica Neue', Helvetica, Arial, sans-serif",
  arial: 'Arial, Helvetica, sans-serif',
  'courier new': "'Courier New', Courier, monospace",
  georgia: "Georgia, 'Times New Roman', serif",
  impact: "Impact, 'Arial Black', sans-serif",
  'lucida console': "'Lucida Console', Monaco, monospace",
  tahoma: 'Tahoma, Arial, sans-serif',
  'times new roman': "'Times New Roman', Times, serif",
  verdana: 'Verdana, Geneva, sans-serif',
  helvetica: 'Helvetica, Arial, sans-serif',
};

/** Shared by Angular previews, published pages, and generated email HTML. */
export function tableTextStyle(
  styles: TableTextStyles | undefined,
  group: TableTextGroup,
  background?: string,
  resolveVariable?: (name: string) => string | undefined,
): Record<string, string> {
  const style = styles?.[group] ?? {};
  const result: Record<string, string> = {};
  if (background !== undefined) result.color = tableTextColor(background, resolveVariable);
  // Do not allow attribute/CSS injection when serializing imported templates to email HTML.
  if (
    typeof style.color === 'string' &&
    /^(#[\da-f]{3,8}|[a-z]+|(?:rgb|hsl)a?\([\d.,%\s]+\)|var\(--[\w-]+\))$/i.test(style.color)
  ) {
    result['color'] = style.color;
  }
  const family = typeof style.fontFamily === 'string' ? FONT_FALLBACKS[style.fontFamily.toLowerCase()] : undefined;
  if (typeof family === 'string') result['font-family'] = family;
  if (typeof style.fontSize === 'number' && Number.isFinite(style.fontSize)) {
    result['font-size'] = `${Math.min(72, Math.max(8, style.fontSize))}px`;
  }
  if (typeof style.bold === 'boolean') result['font-weight'] = style.bold ? '700' : '400';
  if (typeof style.italic === 'boolean') result['font-style'] = style.italic ? 'italic' : 'normal';
  if (typeof style.underline === 'boolean') result['text-decoration'] = style.underline ? 'underline' : 'none';
  return result;
}

/** Choose the more legible of white and the table's dark text against the cell background. */
export function tableTextColor(background: string, resolveVariable?: (name: string) => string | undefined): string {
  let color = (background || '#ffffff').trim();
  for (let depth = 0; depth < 8 && color.startsWith('var('); depth++) {
    const variable = color.match(/^var\(\s*(--[\w-]+)\s*(?:,\s*(.+))?\)$/);
    if (!variable) return '#111827';
    color = (resolveVariable?.(variable[1]) || variable[2] || '#ffffff').trim();
  }
  color = color.toLowerCase();
  const named: Record<string, string> = {
    white: '#ffffff',
    black: '#000000',
    transparent: '#ffffff',
    red: '#ff0000',
    blue: '#0000ff',
    green: '#008000',
  };
  color = named[color] ?? color;
  let channels: number[];
  let alpha = 1;
  if (/^#[\da-f]{3,4}$/i.test(color)) color = '#' + [...color.slice(1)].map(char => char + char).join('');
  if (/^#[\da-f]{6}(?:[\da-f]{2})?$/i.test(color)) {
    channels = [1, 3, 5].map(index => parseInt(color.slice(index, index + 2), 16));
    if (color.length === 9) alpha = parseInt(color.slice(7, 9), 16) / 255;
  } else {
    const rgb = color.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/);
    if (!rgb) return '#111827';
    channels = rgb.slice(1, 4).map(Number);
    alpha = rgb[4] === undefined ? 1 : Math.max(0, Math.min(1, Number(rgb[4])));
  }
  const luminance = (values: number[]) =>
    values.reduce((sum, value, index) => {
      const normalized = Math.max(0, Math.min(255, value)) / 255;
      return (
        sum +
        (normalized <= 0.04045 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4) *
          [0.2126, 0.7152, 0.0722][index]
      );
    }, 0);
  const light = luminance(channels.map(channel => channel * alpha + 255 * (1 - alpha)));
  const dark = luminance([17, 24, 39]);
  const whiteContrast = 1.05 / (light + 0.05);
  const darkContrast = (Math.max(light, dark) + 0.05) / (Math.min(light, dark) + 0.05);
  return whiteContrast > darkContrast ? '#ffffff' : '#111827';
}
